import { RouteConstants } from '@/features/router/RouteConstants';
import { isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import { useWorkspace } from '@/features/workspaces/hooks';
import { setCurrentWorkspace } from '@/features/workspaces/workspacesSlice';
import { EditableTitle } from '@/global/components';
import { Button, Input } from '@/lib';
import { Workspace } from '@/models/workspace';
import { useAppDispatch, RootState } from '@/reducers';
import { useUpdateWorkspaceMutation } from '@/services/endpoints/workspaces';
import { FC } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LocalizationSelector } from './LocalizationSelector';
import { formatInTimeZone } from 'date-fns-tz';
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/lib/constants';

export const WorkspaceDetails: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { termsAccepted } = useSelector((state: RootState) => state.userInfo);
  const isAdmin = isWorkspaceAdmin(workspace.id);
  const history = useHistory();
  const [updateWorkspace, { isLoading: isUpdating }] = useUpdateWorkspaceMutation();

  const handleUpdate = async (item: Workspace, newTitle: string) => {
    const updatedWorkspace = await updateWorkspace({
      workspaceId: item.id,
      workspace: {
        name: newTitle,
        slug: item.slug,
      },
    }).unwrap();
    dispatch(setCurrentWorkspace(updatedWorkspace));
  };

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-end justify-between gap-2'>
        <div className='flex flex-col gap-2'>
          <p className='mb-2 text-sm font-semibold text-secondary-700'>{t('settings:caption_workspace_name')}</p>
          <EditableTitle
            item={workspace}
            title={workspace?.name || ''}
            onUpdate={handleUpdate}
            isLoading={isUpdating}
            isEditable={isAdmin}
            successMessage={{
              title: t('workspaceUpdated'),
              message: t('workspaceUpdatedSuccessfully', { name: workspace?.name }),
            }}
            errorMessage={{
              title: t('workspaceUpdateError'),
              message: t('workspaceUpdateError'),
            }}
          />
        </div>
        <div className='flex flex-row items-end gap-2'>
          <span className='mb-2 text-sm font-normal text-secondary-700'>{t('workspaces:edit.slug')}</span>
          <Input disabled value={workspace?.slug} className='w-full' />
        </div>
      </div>

      {workspace?.companyName && (
        <>
          <p className='mt-16 text-sm font-normal text-secondary-700'>{t('settings:company_caption')}</p>
          <div className='flex flex-row items-end gap-4'>
            <span className=' text-sm font-semibold text-secondary-700'>{workspace.companyName}</span>
            <Button
              intent='link-color'
              text={t('settings:company_view_details')}
              className='p-0'
              onClick={() => history.push(`${RouteConstants.settings}/company`)}
            />
          </div>
        </>
      )}
      <div className='mt-4 flex flex-row items-end gap-4'>
        <LocalizationSelector />
      </div>
      {termsAccepted && (
        <div className='mt-4 text-sm text-secondary-700'>
          <Trans
            i18nKey='settings:accepted_on'
            values={{ date: formatInTimeZone(new Date(termsAccepted), 'UTC', 'dd/MM/yyyy') }}
            components={{
              tc: <a href={TERMS_AND_CONDITIONS_URL} target='_blank' rel='noopener noreferrer' className='underline text-blue-600 hover:text-blue-800' />,
              privacy: <a href={PRIVACY_POLICY_URL} target='_blank' rel='noopener noreferrer' className='underline text-blue-600 hover:text-blue-800' />,
            }}
          />
        </div>
      )}
    </div>
  );
};
